import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface SlurryBriefDto {
  slurryID: number;
  ufSlurryID: string;
  type: string;
  status: string;
  createDate: string;
  comments: string;
  createdBy: string;
  projectName: string;
}

export interface ComplianceTestOptionDto {
  testType: number;
  testTypeId: number;
  testTypeName: string;
}

export interface RedesignReasonOptionDto {
  reasonType: number;
  reasonTypeId: number;
  reasonTypeName: string;
  requiresComplianceTests: boolean;
  availableComplianceTests: ComplianceTestOptionDto[];
}

export interface SlurryRedesignReasonsResponseDto {
  projectType: number;
  reasons: RedesignReasonOptionDto[];
}

export interface SlurryPropertiesDto {
  slurryDensity?: number;
  targetLabVolMl?: number;
  slurryTypeId: number;
  waterFraction?: number;
  comments?: string;
  targetThickeningTimeFrMins?: number;
  targetThickeningTimeToMins?: number;
  measuredDepth?: number;
}

export interface SlurryRedesignReasonInputDto {
  reasonTypeId: number;
  comments?: string;
  complianceTestIds: number[];
  parentSlurryId?: number;
}

export interface AppendNoteResponseDto {
  success: boolean;
  message: string;
  updatedNotes?: string;
}

export interface SlurryCapabilityItemDto {
  allowed: boolean;
  reason?: string | null;
}

export interface SlurryCapabilitiesDto {
  slurryId: number;
  status: number;
  isParentProjectCompleted: boolean;
  viewSlurryPropertiesTests: SlurryCapabilityItemDto;
  viewFoamTests: SlurryCapabilityItemDto;
  viewOtherTests: SlurryCapabilityItemDto;
  updateSlurry: SlurryCapabilityItemDto;
  sendToLab: SlurryCapabilityItemDto;
  revertToDraft: SlurryCapabilityItemDto;
  completeSlurry: SlurryCapabilityItemDto;
  saveTests: SlurryCapabilityItemDto;
  updateMixability: SlurryCapabilityItemDto;
  updateBaseDensity: SlurryCapabilityItemDto;
  updateRedesign: SlurryCapabilityItemDto;
  createBatch: SlurryCapabilityItemDto;
  editBatchExecution: SlurryCapabilityItemDto;
  appendClearNotes: SlurryCapabilityItemDto;
  createFinalReport: SlurryCapabilityItemDto;
  addSingleTest: SlurryCapabilityItemDto;
  removeSingleTest: SlurryCapabilityItemDto;
  editTestCrushLength: SlurryCapabilityItemDto;
}


// ─── Save Slurry (full composition) ─────────────────────────────────────────

/** Mirrors C# SaveSlurryMaterialDto */
export interface SaveSlurryMaterialDto {
  materialId: number;
  /** 'CementBase' | 'PozBase' | 'BlendAdditive' | 'Additive' | 'LiquidBase' | 'LiquidAdditive' */
  sectionType: string;
  basePercent?: number;
  absoluteVolume?: number;
  weight?: number;
  volume?: number;
  calculatedLabWeight?: number;
  specificGravity?: number;
  pozSackWeight?: number;
  /** 1 = BWOC, 2 = BWOW */
  byWeightOfTypeId: number;
  afterBlend: boolean;
  cisProContainerId?: string;
}

/** Mirrors C# SaveSlurryRequestDto */
export interface SaveSlurryRequestDto {
  projectId: number;
  slurryTypeId: number;
  slurryDensity?: number;
  measuredDepth?: number;
  labVolumeMl: number;
  slurryYield?: number;
  totalFluidPercent?: number;
  totalFluidGal?: number;
  equivalentSackWeight?: number;
  notes?: string;
  targetThickeningTimeFrMins?: number;
  targetThickeningTimeToMins?: number;
  cementSackWeight?: number;
  blendCisProContainerId?: string;
  calculatedSg?: number;
  materials: SaveSlurryMaterialDto[];
}

/** Mirrors C# SaveSlurryResponseDto */
export interface SaveSlurryResponseDto {
  slurryId: number;
  ufSlurryId: string;
  projectId: number;
  message: string;
  createdDate: string;
  createdBy: string;
}

/** Mirrors C# SlurryStatusChangeResultDto */
export interface SlurryStatusChangeResult {
  slurryId: number;
  /** SlurryStatuses enum (1..7) */
  status: number;
  statusName: string;
}

/** Lifecycle actions accepted by PUT /slurry/{id}/status. */
export type SlurryStatusAction = 'SendToLab' | 'Revert' | 'Complete';

export interface SlurryStatusChangeRequestDto {
  action: SlurryStatusAction;
  saveBeforeStatusRequest?: SaveSlurryRequestDto;
}

export interface TestOutputStatusChangeRequestDto {
  status: number;
}

export interface TestOutputWorkflowResultDto {
  slurryId: number;
  testOutputMasterId: number;
  slurryBatchId: number;
  action: string;
  status: number;
  statusName: string;
  reviewedBy: string;
  batch: SlurryBatchDto;
}

/** Mirrors C# MixabilityDto. */
export interface MixabilityDto {
  id?: number;
  slurryId?: number;
  score?: number | null;
  timeToWetSolids?: number | null;
  notes?: string | null;
}

export interface UpdateBaseDensityRequestDto {
  baseDensity?: number | null;
}

// ── Test Input Row types (UI models, no TestInputMasterId) ──

export interface TestSharedSettings {
  initialTemperature: number | null;
  bhct: number | null;            // FinalTemperature in WPF
  temperatureRampTime: number | null;
  initialPressure: number | null;
  pressureRampTime: number | null;
  finalPressure: number | null;
  bhst: number | null;
}

export interface ThickeningInputRow {
  bhst: number | null;
  isHesitation: boolean;
  conditioningTime: number | null;
  gradient: number | null;
  isShutdown: boolean;
  stopMotor: number | null;
  startMotor: number | null;
  hasSrt: boolean;
  srt: number | null;
  contamination: boolean;
  notes?: string | null;
  isMuted?: boolean;
}

export interface RheologyRow       { rheologyTypeId: number; bobSleeveTypeId: number | null; temperature: number | null; notes?: string | null; isMuted?: boolean; testInputMasterId?: number | null; outputs?: BatchTestBriefDto[]; }
export interface HpHtRow           { hpHtRheologyTypeId: number | null; temperature: number | null; pressure: number | null; totalTime: number | null; notes?: string | null; isMuted?: boolean; testInputMasterId?: number | null; outputs?: BatchTestBriefDto[]; }
export interface FluidLossRow      { fluidLossTypeId: number | null; temperature: number | null; notes?: string | null; isMuted?: boolean; testInputMasterId?: number | null; outputs?: BatchTestBriefDto[]; }
export interface FreeFluidRow      { angle: number | null; temperature: number | null; notes?: string | null; isMuted?: boolean; testInputMasterId?: number | null; outputs?: BatchTestBriefDto[]; }
export interface StaticGelRow      { criticalStaticGelStrengthTypeId: number; jpt: number | null; finalHydrostaticPressure: number | null; temperature: number | null; csgs: number; notes?: string | null; isMuted?: boolean; testInputMasterId?: number | null; outputs?: BatchTestBriefDto[]; }
export interface CompStrRow        { compressiveStrengthTypeId: number; compressiveStrengthTestTypeId: number; crushTestTypeId: number | null; contamination: boolean; temperatureRampTime: number | null; temperature: number | null; pressure: number | null; lengthOfTest: number | null; notes?: string | null; isMuted?: boolean; testInputMasterId?: number | null; outputs?: BatchTestBriefDto[]; }
export interface FoamStabilityRow  { foamedStabilityTypeId: number; foamedDensity: number | null; notes?: string | null; isMuted?: boolean; testInputMasterId?: number | null; outputs?: BatchTestBriefDto[]; }
export interface HpHtReadingDto    { testInputMasterId?: number | null; temperature?: number | null; pressure?: number | null; totalTime?: number | null; outputs?: BatchTestBriefDto[] | null; }
export interface HpHtTestDto       { hpHtRheologyTypeId?: number | null; notes?: string | null; isMuted?: boolean; readings?: HpHtReadingDto[] | null; }

export interface BatchTestBriefDto {
  slurryBatchId: number;
  batchNumber?: string | null;
  testInputMasterId: number;
  testOutputMasterId: number;
  status: number;
  statusName?: string | null;
  updateUser?: string | null;
  reviewedBy?: string | null;
}

export interface ThickeningOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  ctTemp?: number | null;
  pressure?: number | null;
  batchMix?: number | null;
  initialBc?: number | null;
  bc26?: number | null;
  bc26Total?: number | null;
  bc70?: number | null;
  bc70Total?: number | null;
  bc100?: number | null;
  bc100Total?: number | null;
  timeToCt?: number | null;
  terminationTime?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  endTime?: string | null;
  stopDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
}

export interface RheologyOutputReadingDto {
  rawRpm600?: number | null;
  rawRpm300?: number | null;
  rawRpm200?: number | null;
  rawRpm100?: number | null;
  rawRpm60?: number | null;
  rawRpm30?: number | null;
  rawRpm20?: number | null;
  rawRpm10?: number | null;
  rawRpm6?: number | null;
  rawRpm3?: number | null;
  measuredRpm600?: number | null;
  measuredRpm300?: number | null;
  measuredRpm200?: number | null;
  measuredRpm100?: number | null;
  measuredRpm60?: number | null;
  measuredRpm30?: number | null;
  measuredRpm20?: number | null;
  measuredRpm10?: number | null;
  measuredRpm6?: number | null;
  measuredRpm3?: number | null;
}

export interface RheologyOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temperature?: number | null;
  springFactor?: number | null;
  k1?: number | null;
  k2?: number | null;
  r10SecGelStrength?: number | null;
  r10MinGelStrength?: number | null;
  startedBy?: string | null;
  stoppedBy?: string | null;
  startDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
  readings: RheologyOutputReadingDto[];
}

export interface HpHtRheologyDirectionalReadingDto {
  measuredRpm600?: number | null;
  measuredRpm300?: number | null;
  measuredRpm200?: number | null;
  measuredRpm100?: number | null;
  measuredRpm60?: number | null;
  measuredRpm30?: number | null;
  measuredRpm20?: number | null;
  measuredRpm10?: number | null;
  measuredRpm6?: number | null;
  measuredRpm3?: number | null;
  measured10Sec?: number | null;
  measured10Min?: number | null;
}

export interface HpHtRheologyOutputReadingDto {
  inputReadingId?: number | null;
  temperature?: number | null;
  pressure?: number | null;
  time?: number | null;
  springFactor?: number | null;
  measured10Sec?: number | null;
  measured10Min?: number | null;
  rampUp: HpHtRheologyDirectionalReadingDto;
  rampDown: HpHtRheologyDirectionalReadingDto;
  average: HpHtRheologyDirectionalReadingDto;
  calculatedRampUp?: HpHtRheologyDirectionalReadingDto | null;
  calculatedRampDown?: HpHtRheologyDirectionalReadingDto | null;
  calculatedAverage?: HpHtRheologyDirectionalReadingDto | null;
}

export interface HpHtRheologyOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  equipmentTypeId?: number | null;
  startedBy?: string | null;
  stoppedBy?: string | null;
  startDate?: string | null;
  stopDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
  readings: HpHtRheologyOutputReadingDto[];
}

export interface FluidLossOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temp?: number | null;
  filtrateVol05Min?: number | null;
  filtrateVol1Min?: number | null;
  filtrateVol5Min?: number | null;
  filtrateVol10Min?: number | null;
  filtrateVol15Min?: number | null;
  filtrateVol20Min?: number | null;
  filtrateVol30Min?: number | null;
  totalFiltrateVol?: number | null;
  totalTestTime?: number | null;
  fluidLossCalculated?: number | null;
  startedBy?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
}

export interface FreeFluidOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temp?: number | null;
  mlsff?: number | null;
  percentageFf?: number | null;
  angle?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
}

export interface StaticGelOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temperature?: number | null;
  pressure?: number | null;
  timeto100lbs?: number | null;
  timeto500lbs?: number | null;
  transitionTime?: string | null;
  timetoCsgs?: number | null;
  jpt?: number | null;
  csgsPeriod?: number | null;
  totalTimeTo100lbs?: number | null;
  totalTimeTo500lbs?: number | null;
  totalTimeToCsgs?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  endTime?: string | null;
  stopDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
}

export interface CompressiveStrengthOutputReadingDto {
  cubeNo?: number | null;
  width?: number | null;
  diameter?: number | null;
  height?: number | null;
  area?: number | null;
  force?: number | null;
  psi?: number | null;
  isInclude?: boolean | null;
}

export interface CompressiveStrengthOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  compressiveStrengthTypeId: number;
  lengthOfTest?: number | null;
  temperature?: number | null;
  pressure?: number | null;
  psi100?: number | null;
  psi500?: number | null;
  condTime?: number | null;
  condTemp?: number | null;
  testTemp?: number | null;
  hr8Psi?: number | null;
  hr12Psi?: number | null;
  day1Psi?: number | null;
  day2Psi?: number | null;
  endPsi?: number | null;
  endTime?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  stopTime?: string | null;
  stopDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
  readings: CompressiveStrengthOutputReadingDto[];
}

export interface FoamStabilityOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  foamedStabilityTypeId: number;
  temp?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
  stable?: string | null;
  density?: number | null;
}

export interface AcidSolubilityOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temp?: number | null;
  initialMass?: number | null;
  hr1?: number | null;
  hr4?: number | null;
  hr8?: number | null;
  hr12?: number | null;
  day1?: number | null;
  day2?: number | null;
  day3?: number | null;
  day4?: number | null;
  day7?: number | null;
  hr1Percent?: number | null;
  hr4Percent?: number | null;
  hr8Percent?: number | null;
  hr12Percent?: number | null;
  day1Percent?: number | null;
  day2Percent?: number | null;
  day3Percent?: number | null;
  day4Percent?: number | null;
  day7Percent?: number | null;
  startedBy?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
}

export interface BasicCrushOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  loadRate?: number | null;
  csValue?: number | null;
  startedBy?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
}

export interface ApiSedimentationOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temp?: number | null;
  pressure?: number | null;
  curingTime?: number | null;
  piece1?: number | null;
  piece2?: number | null;
  piece3?: number | null;
  piece4?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  stopTime?: string | null;
  stopDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
}

export interface ExpansionOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temp?: number | null;
  pressure?: number | null;
  curingTime?: number | null;
  percentExpansion?: number | null;
  percentShrinkage?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  stopTime?: string | null;
  stopDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
}

export interface PressurizedMudScaleOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  ppg?: number | null;
  density?: number | null;
  startedBy?: string | null;
  startDate?: string | null;
  testDate?: string | null;
  comments?: string | null;
}

export interface PycnometerOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  sampleId?: number | null;
  specificGravity?: number | null;
  averageDensity?: number | null;
  rVolume?: number | null;
  cVolume?: number | null;
  startedBy?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
}

export interface ShearBondOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  temp?: number | null;
  pressure?: number | null;
  curingTime?: number | null;
  strength?: number | null;
  startedBy?: string | null;
  startTime?: string | null;
  startDate?: string | null;
  stoppedBy?: string | null;
  stopTime?: string | null;
  stopDate?: string | null;
  equipmentNumber?: string | null;
  comments?: string | null;
}

export interface SplittingTsOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  stsValue1?: number | null;
  stsValue2?: number | null;
  stsValue3?: number | null;
  stsValue4?: number | null;
  stsValue5?: number | null;
  stsValue6?: number | null;
  startedBy?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
}

export interface TriaxialConfinedCsOutputReadingDto {
  readingId?: number | null;
  groupIndex?: number | null;
  sequence?: number | null;
  loadRate?: number | null;
  confPress?: number | null;
  temp?: number | null;
  youngModulus?: number | null;
  pr?: number | null;
  csValue?: number | null;
}

export interface TriaxialConfinedCsOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  startedBy?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
  readings: TriaxialConfinedCsOutputReadingDto[];
}

export interface UnconfinedCsOutputReadingDto {
  readingId?: number | null;
  groupIndex?: number | null;
  sequence?: number | null;
  loadRate?: number | null;
  youngModulus?: number | null;
  pr?: number | null;
  csValue?: number | null;
}

export interface UnconfinedCsOutputDto {
  testOutputMasterId: number;
  slurryBatchId: number;
  batchNumber?: string | null;
  status: number;
  startedBy?: string | null;
  stoppedBy?: string | null;
  comments?: string | null;
  readings: UnconfinedCsOutputReadingDto[];
}

export interface TestInputBaseDto {
  testInputMasterId?: number;
  outputs?: BatchTestBriefDto[];
  notes?: string;
  isMuted?: boolean;
}

/** Mirrors C# SlurryTestInputDto for sending to PUT /slurry/{id}/tests. */
export interface SlurryTestInputDto {
  sharedSettings: {
    initialTemperature: number; finalTemperature: number; temperatureRampTime: number;
    initialPressure: number; pressureRampTime: number; finalPressure: number; bhst: number;
    baseDensity?: number; foamDensity?: number;
  } | null;
  thickeningInput: (TestInputBaseDto & {
    bhst?: number; gradient?: number; stopMotor?: number; startMotor?: number;
    isHesitation?: boolean; isShutdown?: boolean; conditioningTime?: number;
    contamination: boolean; hasSrt?: boolean; srt?: number;
    notes?: string; isMuted?: boolean;
  }) | null;
  rheologyInputs: Array<TestInputBaseDto & { temperature?: number; rheologyTypeId: number; bobSleeveTypeId?: number }>;
  hpHtRheologyTests: HpHtTestDto[];
  fluidLossInputs: Array<TestInputBaseDto & { fluidLossTypeId?: number; temperature?: number }>;
  freeFluidInputs: Array<TestInputBaseDto & { angle?: number; temperature?: number }>;
  staticGelInputs: Array<TestInputBaseDto & { criticalStaticGelStrengthTypeId: number; jpt?: number; finalHydrostaticPressure?: number; temperature?: number; csgs: number }>;
  compressiveStrengthInputs: Array<TestInputBaseDto & { compressiveStrengthTypeId: number; compressiveStrengthTestTypeId: number; crushTestTypeId?: number; contamination: boolean; temperatureRampTime?: number; temperature?: number; pressure?: number; lengthOfTest?: number }>;
  foamCompressiveStrengthInputs: Array<TestInputBaseDto & { compressiveStrengthTypeId: number; compressiveStrengthTestTypeId: number; crushTestTypeId?: number; contamination: boolean; temperatureRampTime?: number; temperature?: number; pressure?: number; lengthOfTest?: number }>;
  foamStabilityInputs: Array<TestInputBaseDto & { foamedStabilityTypeId: number; foamedDensity?: number }>;
  apiSedimentationInputs: TestInputBaseDto[];
  expansionInputs: TestInputBaseDto[];
  pressurizedMudScaleInputs: TestInputBaseDto[];
  pycnometerInputs: TestInputBaseDto[];
  shearBondInputs: Array<TestInputBaseDto & { cureTimeHrs?: number }>;
  acidSolubilityInputs: TestInputBaseDto[];
  splittingTsInputs: TestInputBaseDto[];
  unconfinedCsInputs: TestInputBaseDto[];
  triaxilConfinedCsInputs: TestInputBaseDto[];
  basicCrushTestInputs: TestInputBaseDto[];
}

/** Mirrors C# SlurryDto (header + parameters) returned by GET /slurry/{id}. */
export interface SlurryDetailDto {
  id: number;
  ufSlurryID: string;
  /** Compatibility with API versions that serialize the final acronym as `Id`. */
  ufSlurryId?: string;
  cementProjID: number;
  isParentProjectCompleted?: boolean;
  /** SlurryStatuses enum (1=Draft … 7=SentToLabAgain) */
  status: number;
  /** SlurryTypes enum */
  type: number;
  targetThickeningTimeFrMins?: number;
  targetThickeningTimeToMins?: number;
  measuredDepth?: number;
  jobType?: string;
  jobSubtype?: string;
  slurryDensity?: number;
  targetLabVolume?: number;
  equivalentSackWeight?: number;
  cementSackWeight?: number;
  comments?: string;
  isBlendTest?: boolean;
  pipeSize?: string;
  rigName?: string;
  wellName?: string;
  projectReceivedBy?: string;
  sentToLabDate?: string | null;
  preferredBlendCisPROContainerID?: string;
  preferredBlendCisPROContainerLocation?: string;
  preferredCisPROContainerIDsHaveDeleted?: boolean;
  /** Material composition (dry + liquid), enriched for editing. */
  materials?: SlurryDetailMaterialDto[];
}

/** Mirrors C# SlurryMaterialDto (enriched for editing). */
export interface SlurryDetailMaterialDto {
  materialId: number;
  materialName?: string;
  materialTypeId: number;
  /** CementBase | PozBase | BlendAdditive | Additive | LiquidBase | LiquidAdditive */
  sectionType?: string;
  basePercent?: number;
  absoluteVolume?: number;
  density?: number;
  weight?: number;
  volume?: number;
  calculatedLabWeight?: number;
  specificGravity?: number;
  overRideSp?: number;
  sackWeight?: number;
  pozSackWeight?: number;
  byWeightOfTypeId: number;
  afterBlend: boolean;
  preferredCisProContainerId?: string;
  preferredCisProContainerLocation?: string;
  preferredCisProContainerIdsHaveDeleted?: boolean;
  containerLocation?: string;
}

/** Mirrors C# SlurryBatchDto. */
export interface SlurryBatchDto {
  slurryBatchId: number;
  batchNumber?: string;
  startBy?: string;
  notes?: string;
  slurryId: number;
  isBlendTest?: boolean;
  targetLabVolume?: number | null;
  batchLabVolume?: number | null;
  foamDensity?: number | null;
  measuredBlendWeight?: number | null;
  measurementTolerance?: number | null;
  excessFoamPercent?: number | null;
  preferredBlendCisProcontainerId?: string | null;
  preferredBlendCisProcontainerDeleted: boolean;
  capturedBlendCisProcontainerDeleted: boolean;
  capturedBlendCisProcontainerId?: string | null;
  replacePreferredBlendCisProcontainerId?: boolean | null;
  blendContainerComments?: string | null;
  blendWeightComments?: string | null;
  measuredMudCisProcontainer?: string | null;
  selectedFoamerMaterialIds?: number[];
  mudMeasurementAccessible?: boolean;
  updateDate?: string;
  tests: { slurryBatchId: number; testInputMasterId: number; testOutputMasterId?: number; status: number }[];
  materialOptions?: BatchMaterialOptionDto[];
}

export interface BatchMaterialOptionDto {
  slurryMaterialId: number;
  materialId: number;
  materialName: string;
  sectionType: string;
  afterBlend: boolean;
  calculatedLabWeight?: number | null;
  isMeasurementRequired?: boolean | null;
  preferredCisProcontainerId?: string | null;
  preferredCisProcontainerDeleted: boolean;
  isFluidLossTestWithFoamNeeded: boolean;
}

export interface CreateBatchRequestDto {
  testInputMasterIds: number[];
  notes?: string;
  batchLabVolume?: number;
  foamDensity?: number;
}

export interface AddSingleTestRequestDto {
  testTypeId: number;
  notes?: string;
  bhst?: number | null;
  gradient?: number | null;
  stopMotor?: number | null;
  startMotor?: number | null;
  isHesitation?: boolean;
  isShutdown?: boolean;
  conditioningTime?: number | null;
  contamination?: boolean;
  hasSrt?: boolean;
  srt?: number | null;
  rheologyTypeId?: number | null;
  bobSleeveTypeId?: number | null;
  temperature?: number | null;
  hpHtRheologyTypeId?: number | null;
  hpHtReadings?: AddSingleTestHpHtReadingDto[];
  hpHtTemperature?: number | null;
  hpHtPressure?: number | null;
  hpHtTotalTime?: number | null;
  fluidLossTypeId?: number | null;
  angle?: number | null;
  criticalStaticGelStrengthTypeId?: number | null;
  jpt?: number | null;
  finalHydrostaticPressure?: number | null;
  csgs?: number | null;
  compressiveStrengthTypeId?: number | null;
  crushTestTypeId?: number | null;
  temperatureRampTime?: number | null;
  pressure?: number | null;
  lengthOfTest?: number | null;
  foamedStabilityTypeId?: number | null;
  foamedDensity?: number | null;
  cureTimeHrs?: number | null;
}

export interface AddSingleTestResponseDto {
  testInputMasterId: number;
  testTypeId: number;
  message: string;
}

export interface AddSingleTestHpHtReadingDto {
  temperature?: number | null;
  pressure?: number | null;
  totalTime?: number | null;
}

export interface UpdateBatchRequestDto {
  notes?: string;
  batchLabVolume?: number;
  foamDensity?: number;
  measuredBlendWeight?: number;
  measurementTolerance?: number;
  excessFoamPercent?: number;
  capturedBlendCisProcontainerId?: string;
  replacePreferredBlendCisProcontainerId?: boolean;
  blendContainerComments?: string;
  blendWeightComments?: string;
  measuredMudCisProcontainer?: string;
  selectedFoamerMaterialIds?: number[];
  selectedTestInputMasterIds?: number[];
}

export interface BatchExecutionResultDto {
  slurryId: number;
  slurryBatchId: number;
  action: string;
  updatedOutputsCount: number;
  batch: SlurryBatchDto;
}

export interface SubmitBatchRequestDto {
  batch: UpdateBatchRequestDto;
  measuredMaterials: UpsertMeasuredMaterialRequestDto[];
  measuredFoam?: UpsertMeasuredFoamRequestDto;
  measuredMud?: UpsertMeasuredMudRequestDto;
}

export interface MeasuredMaterialDto {
  measuredMaterialId: number;
  slurryBatchId: number;
  slurryMaterialId: number;
  measuredAmount?: number | null;
  overrideAmount?: number | null;
  caputuredCisProcontainerId?: string;
  caputuredCisProcontainerDeleted: boolean;
  isFoamer: boolean;
  containerComments?: string | null;
  weightComments?: string | null;
  preferredCisProcontainerId?: string | null;
  preferredCisProcontainerDeleted: boolean;
  replacePreferredCisProcontainerId?: boolean | null;
  updateDate?: string | null;
  updateUser?: string | null;
}

export interface UpsertMeasuredMaterialRequestDto {
  slurryMaterialId: number;
  measuredAmount?: number;
  overrideAmount?: number;
  caputuredCisProcontainerId?: string;
  isFoamer: boolean;
  containerComments?: string;
  weightComments?: string;
  preferredCisProcontainerId?: string;
  replacePreferredCisProcontainerId?: boolean;
}

export interface MeasuredFoamDto {
  measuredFoamId: number;
  slurryBatchId: number;
  measuredWeight?: number | null;
  cupWeight?: number | null;
  cupVolume?: number | null;
  weightComments?: string | null;
}

export interface UpsertMeasuredFoamRequestDto {
  measuredWeight?: number;
  cupWeight?: number;
  cupVolume?: number;
  weightComments?: string;
}

export interface MeasuredMudDto {
  slurryBatchId: number;
  measuredMudCisProcontainer?: string | null;
  mudContainerComments?: string | null;
  preferredMudCisProcontainer?: string | null;
  preferredMudCisProcontainerDeleted: boolean;
  measuredMudCisProcontainerDeleted: boolean;
  replacePreferredMudCisProcontainer?: boolean | null;
  updateDate?: string | null;
  updateUser?: string | null;
}

export interface UpsertMeasuredMudRequestDto {
  measuredMudCisProcontainer?: string;
  mudContainerComments?: string;
  preferredMudCisProcontainer?: string;
  replacePreferredMudCisProcontainer?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SlurryService {
     constructor(private http: HttpClient) {
      }
      private readonly baseUrl = environment.apiUrl;

  getBriefByProjectId(projectId: number) : Observable<SlurryBriefDto[]> {
    return this.http.get<SlurryBriefDto[]>(`${this.baseUrl}/slurry/slurry-brief/${projectId}`);
  }

  /**
   * Loads a single slurry (header + parameters + tests) for editing.
   * GET /api/v1/slurry/{id}
   */
  getById(slurryId: number): Observable<SlurryDetailDto> {
    return this.http.get<SlurryDetailDto>(`${this.baseUrl}/slurry/${slurryId}`);
  }

  getCapabilities(slurryId: number): Observable<SlurryCapabilitiesDto> {
    return this.http.get<SlurryCapabilitiesDto>(`${this.baseUrl}/slurry/${slurryId}/capabilities`);
  }

  getRedesignReasons(projectType: number): Observable<SlurryRedesignReasonsResponseDto> {
    return this.http.get<SlurryRedesignReasonsResponseDto>(
      `${this.baseUrl}/slurry/redesign-reasons/${projectType}`
    );
  }

  /**
   * Saves a new slurry with its full material composition.
   * POST /api/v1/slurry/save
   * Mirrors WPF SaveSlurryAsync → CreateAsync → Create(DesignMixObject).
   */
  saveSlurry(request: SaveSlurryRequestDto): Observable<SaveSlurryResponseDto> {
    return this.http.post<SaveSlurryResponseDto>(`${this.baseUrl}/slurry/save`, request);
  }

  /**
   * Updates an existing slurry and replaces its material composition.
   * PUT /api/v1/slurry/{id}
   */
  updateSlurry(slurryId: number, request: SaveSlurryRequestDto): Observable<SaveSlurryResponseDto> {
    return this.http.put<SaveSlurryResponseDto>(`${this.baseUrl}/slurry/${slurryId}`, request);
  }

  /**
   * Changes a slurry's lifecycle status. The target status is computed server-side.
   * PUT /api/v1/slurry/{id}/status
   */
  updateStatus(slurryId: number, action: SlurryStatusAction, saveBeforeStatusRequest?: SaveSlurryRequestDto): Observable<SlurryStatusChangeResult> {
    const request: SlurryStatusChangeRequestDto = { action, saveBeforeStatusRequest };
    return this.http.put<SlurryStatusChangeResult>(`${this.baseUrl}/slurry/${slurryId}/status`, request);
  }

  // ── Mixability ──
  getMixability(slurryId: number): Observable<MixabilityDto | null> {
    return this.http.get<MixabilityDto | null>(`${this.baseUrl}/slurry/${slurryId}/mixability`);
  }

  updateMixability(slurryId: number, dto: MixabilityDto): Observable<MixabilityDto> {
    return this.http.put<MixabilityDto>(`${this.baseUrl}/slurry/${slurryId}/mixability`, dto);
  }

  updateBaseDensity(slurryId: number, dto: UpdateBaseDensityRequestDto): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/slurry/${slurryId}/base-density`, dto);
  }

  // ── Redesign reason ──
  getRedesign(slurryId: number): Observable<SlurryRedesignReasonInputDto | null> {
    return this.http.get<SlurryRedesignReasonInputDto | null>(`${this.baseUrl}/slurry/${slurryId}/redesign`);
  }

  updateRedesign(slurryId: number, dto: SlurryRedesignReasonInputDto): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/slurry/${slurryId}/redesign`, dto);
  }

  // ── Tests ──
  getTests(slurryId: number): Observable<SlurryTestInputDto | null> {
    return this.http.get<SlurryTestInputDto | null>(`${this.baseUrl}/slurry/${slurryId}/tests`);
  }

  saveTests(slurryId: number, dto: SlurryTestInputDto): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/slurry/${slurryId}/tests`, dto);
  }

  addSingleTest(slurryId: number, request: AddSingleTestRequestDto): Observable<AddSingleTestResponseDto> {
    return this.http.post<AddSingleTestResponseDto>(`${this.baseUrl}/slurry/${slurryId}/tests/single`, request);
  }

  removeSingleTest(slurryId: number, testInputMasterId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/slurry/${slurryId}/tests/${testInputMasterId}`);
  }

  // ── Batches ──
  getBatches(slurryId: number): Observable<SlurryBatchDto[]> {
    return this.http.get<SlurryBatchDto[]>(`${this.baseUrl}/slurry/${slurryId}/batches`);
  }

  createBatch(slurryId: number, request: CreateBatchRequestDto): Observable<SlurryBatchDto> {
    return this.http.post<SlurryBatchDto>(`${this.baseUrl}/slurry/${slurryId}/batches`, request);
  }

  getBatch(slurryId: number, batchId: number): Observable<SlurryBatchDto> {
    return this.http.get<SlurryBatchDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}`);
  }

  getThickeningOutput(slurryId: number, testOutputMasterId: number): Observable<ThickeningOutputDto> {
    return this.http.get<ThickeningOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/thickening`);
  }

  saveThickeningOutput(slurryId: number, testOutputMasterId: number, request: ThickeningOutputDto): Observable<ThickeningOutputDto> {
    return this.http.put<ThickeningOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/thickening`, request);
  }

  getFluidLossOutput(slurryId: number, testOutputMasterId: number): Observable<FluidLossOutputDto> {
    return this.http.get<FluidLossOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/fluid-loss`);
  }

  saveFluidLossOutput(slurryId: number, testOutputMasterId: number, request: FluidLossOutputDto): Observable<FluidLossOutputDto> {
    return this.http.put<FluidLossOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/fluid-loss`, request);
  }

  getFreeFluidOutput(slurryId: number, testOutputMasterId: number): Observable<FreeFluidOutputDto> {
    return this.http.get<FreeFluidOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/free-fluid`);
  }

  saveFreeFluidOutput(slurryId: number, testOutputMasterId: number, request: FreeFluidOutputDto): Observable<FreeFluidOutputDto> {
    return this.http.put<FreeFluidOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/free-fluid`, request);
  }

  getStaticGelOutput(slurryId: number, testOutputMasterId: number): Observable<StaticGelOutputDto> {
    return this.http.get<StaticGelOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/static-gel`);
  }

  saveStaticGelOutput(slurryId: number, testOutputMasterId: number, request: StaticGelOutputDto): Observable<StaticGelOutputDto> {
    return this.http.put<StaticGelOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/static-gel`, request);
  }

  getCompressiveStrengthOutput(slurryId: number, testOutputMasterId: number): Observable<CompressiveStrengthOutputDto> {
    return this.http.get<CompressiveStrengthOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/compressive-strength`);
  }

  saveCompressiveStrengthOutput(slurryId: number, testOutputMasterId: number, request: CompressiveStrengthOutputDto): Observable<CompressiveStrengthOutputDto> {
    return this.http.put<CompressiveStrengthOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/compressive-strength`, request);
  }

  getFoamStabilityOutput(slurryId: number, testOutputMasterId: number): Observable<FoamStabilityOutputDto> {
    return this.http.get<FoamStabilityOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/foam-stability`);
  }

  saveFoamStabilityOutput(slurryId: number, testOutputMasterId: number, request: FoamStabilityOutputDto): Observable<FoamStabilityOutputDto> {
    return this.http.put<FoamStabilityOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/foam-stability`, request);
  }

  getAcidSolubilityOutput(slurryId: number, testOutputMasterId: number): Observable<AcidSolubilityOutputDto> {
    return this.http.get<AcidSolubilityOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/acid-solubility`);
  }

  saveAcidSolubilityOutput(slurryId: number, testOutputMasterId: number, request: AcidSolubilityOutputDto): Observable<AcidSolubilityOutputDto> {
    return this.http.put<AcidSolubilityOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/acid-solubility`, request);
  }

  getBasicCrushOutput(slurryId: number, testOutputMasterId: number): Observable<BasicCrushOutputDto> {
    return this.http.get<BasicCrushOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/basic-crush`);
  }

  saveBasicCrushOutput(slurryId: number, testOutputMasterId: number, request: BasicCrushOutputDto): Observable<BasicCrushOutputDto> {
    return this.http.put<BasicCrushOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/basic-crush`, request);
  }

  getApiSedimentationOutput(slurryId: number, testOutputMasterId: number): Observable<ApiSedimentationOutputDto> {
    return this.http.get<ApiSedimentationOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/api-sedimentation`);
  }

  saveApiSedimentationOutput(slurryId: number, testOutputMasterId: number, request: ApiSedimentationOutputDto): Observable<ApiSedimentationOutputDto> {
    return this.http.put<ApiSedimentationOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/api-sedimentation`, request);
  }

  getExpansionOutput(slurryId: number, testOutputMasterId: number): Observable<ExpansionOutputDto> {
    return this.http.get<ExpansionOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/expansion`);
  }

  saveExpansionOutput(slurryId: number, testOutputMasterId: number, request: ExpansionOutputDto): Observable<ExpansionOutputDto> {
    return this.http.put<ExpansionOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/expansion`, request);
  }

  getPressurizedMudScaleOutput(slurryId: number, testOutputMasterId: number): Observable<PressurizedMudScaleOutputDto> {
    return this.http.get<PressurizedMudScaleOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/pressurized-mud-scale`);
  }

  savePressurizedMudScaleOutput(slurryId: number, testOutputMasterId: number, request: PressurizedMudScaleOutputDto): Observable<PressurizedMudScaleOutputDto> {
    return this.http.put<PressurizedMudScaleOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/pressurized-mud-scale`, request);
  }

  getPycnometerOutput(slurryId: number, testOutputMasterId: number): Observable<PycnometerOutputDto> {
    return this.http.get<PycnometerOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/pycnometer`);
  }

  savePycnometerOutput(slurryId: number, testOutputMasterId: number, request: PycnometerOutputDto): Observable<PycnometerOutputDto> {
    return this.http.put<PycnometerOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/pycnometer`, request);
  }

  getShearBondOutput(slurryId: number, testOutputMasterId: number): Observable<ShearBondOutputDto> {
    return this.http.get<ShearBondOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/shear-bond`);
  }

  saveShearBondOutput(slurryId: number, testOutputMasterId: number, request: ShearBondOutputDto): Observable<ShearBondOutputDto> {
    return this.http.put<ShearBondOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/shear-bond`, request);
  }

  getSplittingTsOutput(slurryId: number, testOutputMasterId: number): Observable<SplittingTsOutputDto> {
    return this.http.get<SplittingTsOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/splitting-ts`);
  }

  saveSplittingTsOutput(slurryId: number, testOutputMasterId: number, request: SplittingTsOutputDto): Observable<SplittingTsOutputDto> {
    return this.http.put<SplittingTsOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/splitting-ts`, request);
  }

  getTriaxialConfinedCsOutput(slurryId: number, testOutputMasterId: number): Observable<TriaxialConfinedCsOutputDto> {
    return this.http.get<TriaxialConfinedCsOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/triaxial-confined-cs`);
  }

  saveTriaxialConfinedCsOutput(slurryId: number, testOutputMasterId: number, request: TriaxialConfinedCsOutputDto): Observable<TriaxialConfinedCsOutputDto> {
    return this.http.put<TriaxialConfinedCsOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/triaxial-confined-cs`, request);
  }

  getUnconfinedCsOutput(slurryId: number, testOutputMasterId: number): Observable<UnconfinedCsOutputDto> {
    return this.http.get<UnconfinedCsOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/unconfined-cs`);
  }

  saveUnconfinedCsOutput(slurryId: number, testOutputMasterId: number, request: UnconfinedCsOutputDto): Observable<UnconfinedCsOutputDto> {
    return this.http.put<UnconfinedCsOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/unconfined-cs`, request);
  }

  getRheologyOutput(slurryId: number, testOutputMasterId: number): Observable<RheologyOutputDto> {
    return this.http.get<RheologyOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/rheology`);
  }

  saveRheologyOutput(slurryId: number, testOutputMasterId: number, request: RheologyOutputDto): Observable<RheologyOutputDto> {
    return this.http.put<RheologyOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/rheology`, request);
  }

  getHpHtRheologyOutput(slurryId: number, testOutputMasterId: number): Observable<HpHtRheologyOutputDto> {
    return this.http.get<HpHtRheologyOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/hpht-rheology`);
  }

  saveHpHtRheologyOutput(slurryId: number, testOutputMasterId: number, request: HpHtRheologyOutputDto): Observable<HpHtRheologyOutputDto> {
    return this.http.put<HpHtRheologyOutputDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/hpht-rheology`, request);
  }

  changeTestOutputStatus(slurryId: number, testOutputMasterId: number, status: number): Observable<TestOutputWorkflowResultDto> {
    return this.http.post<TestOutputWorkflowResultDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/status`, { status });
  }

  revertTestOutput(slurryId: number, testOutputMasterId: number): Observable<TestOutputWorkflowResultDto> {
    return this.http.post<TestOutputWorkflowResultDto>(`${this.baseUrl}/slurry/${slurryId}/test-outputs/${testOutputMasterId}/revert`, {});
  }

  updateBatch(slurryId: number, batchId: number, request: UpdateBatchRequestDto): Observable<SlurryBatchDto> {
    return this.http.put<SlurryBatchDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}`, request);
  }

  submitBatch(slurryId: number, batchId: number, request: SubmitBatchRequestDto): Observable<BatchExecutionResultDto> {
    return this.http.put<BatchExecutionResultDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/submit`, request);
  }

  redoBatch(slurryId: number, batchId: number): Observable<BatchExecutionResultDto> {
    return this.http.put<BatchExecutionResultDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/redo`, {});
  }

  redoDryMix(slurryId: number, batchId: number): Observable<BatchExecutionResultDto> {
    return this.http.put<BatchExecutionResultDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/redo-dry`, {});
  }

  redoLiquidMix(slurryId: number, batchId: number): Observable<BatchExecutionResultDto> {
    return this.http.put<BatchExecutionResultDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/redo-liquid`, {});
  }

  redoFoamMix(slurryId: number, batchId: number): Observable<BatchExecutionResultDto> {
    return this.http.put<BatchExecutionResultDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/redo-foam`, {});
  }

  redoMudMix(slurryId: number, batchId: number): Observable<BatchExecutionResultDto> {
    return this.http.put<BatchExecutionResultDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/redo-mud`, {});
  }

  getMeasuredMaterials(slurryId: number, batchId: number): Observable<MeasuredMaterialDto[]> {
    return this.http.get<MeasuredMaterialDto[]>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/measurements/materials`);
  }

  upsertMeasuredMaterial(slurryId: number, batchId: number, request: UpsertMeasuredMaterialRequestDto): Observable<MeasuredMaterialDto> {
    return this.http.put<MeasuredMaterialDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/measurements/materials`, request);
  }

  getMeasuredFoam(slurryId: number, batchId: number): Observable<MeasuredFoamDto | null> {
    return this.http.get<MeasuredFoamDto | null>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/measurements/foam`);
  }

  upsertMeasuredFoam(slurryId: number, batchId: number, request: UpsertMeasuredFoamRequestDto): Observable<MeasuredFoamDto> {
    return this.http.put<MeasuredFoamDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/measurements/foam`, request);
  }

  getMeasuredMud(slurryId: number, batchId: number): Observable<MeasuredMudDto> {
    return this.http.get<MeasuredMudDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/measurements/mud`);
  }

  upsertMeasuredMud(slurryId: number, batchId: number, request: UpsertMeasuredMudRequestDto): Observable<MeasuredMudDto> {
    return this.http.put<MeasuredMudDto>(`${this.baseUrl}/slurry/${slurryId}/batches/${batchId}/measurements/mud`, request);
  }

  getSlurryNotes(slurryId: number): Observable<string> {
    return this.http.get(`${this.baseUrl}/slurry/${slurryId}/notes`, { responseType: 'text' });
  }

  appendSlurryNote(slurryId: number, note: string): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.baseUrl}/slurry/${slurryId}/notes/append`, { note });
  }

  clearSlurryNote(slurryId: number): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.baseUrl}/slurry/${slurryId}/notes/clear`, {});
  }

  appendTestNote(slurryId: number, testInputMasterId: number, note: string): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.baseUrl}/slurry/${slurryId}/tests/${testInputMasterId}/notes/append`, { note });
  }

  clearTestNote(slurryId: number, testInputMasterId: number): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.baseUrl}/slurry/${slurryId}/tests/${testInputMasterId}/notes/clear`, {});
  }
}